import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Tag, Leaf, Package, Sprout } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const ICONS = { Oliviers: Sprout, Arbres: Leaf, Arbustes: Leaf, Plantes: Sprout, "Matériaux": Package };

function Price({ p }) {
  if (p.group_price != null) return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="font-display text-2xl text-brand">{p.group_price.toFixed(2)} €</span>
      <span className="text-sm text-stone line-through">{Number(p.list_price ?? p.price).toFixed(2)} €</span>
      <span className="text-[10px] font-bold bg-brand/10 text-brand rounded-full px-2 py-0.5" data-testid={`group-badge-${p.slug}`}>-{p.group_discount_percent}%{p.group_name ? ` ${p.group_name}` : ""}</span>
    </span>
  );
  if (p.on_sale) return (
    <span className="flex items-baseline gap-2">
      <span className="font-display text-2xl text-terracotta">{p.effective_price.toFixed(2)} €</span>
      <span className="text-sm text-stone line-through">{p.price.toFixed(2)} €</span>
    </span>
  );
  return <span className="font-display text-2xl text-forest">{p.price.toFixed(2)} €</span>;
}

function Card({ p, add }) {
  return (
    <div className="group flex flex-col h-full rounded-[22px] overflow-hidden bg-white border border-border hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest/5 transition-all" data-testid={`product-${p.slug}`}>
      <Link to={`/boutique/${p.slug}`} className="relative aspect-[4/3] overflow-hidden bg-sage">
        {p.image && <img src={fileUrl(p.image)} alt={p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />}
        {p.on_sale && <span className="absolute top-3 left-3 rounded-full bg-terracotta text-cream text-xs font-bold px-3 py-1 flex items-center gap-1"><Tag size={12} /> Offre</span>}
        {p.stock <= 0 && <span className="absolute top-3 right-3 rounded-full bg-stone/90 text-cream text-xs px-3 py-1">Rupture</span>}
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <span className="text-[11px] font-bold uppercase tracking-wider text-terracotta">{p.category}</span>
        <Link to={`/boutique/${p.slug}`}><h3 className="mt-1 font-display text-xl text-forest leading-tight hover:text-brand transition-colors">{p.name}</h3></Link>
        <p className="text-sm text-stone mt-2 line-clamp-2 flex-1">{p.description}</p>
        <div className="mt-4 flex items-center justify-between">
          <Price p={p} />
          <button onClick={() => { add(p); toast.success(`${p.name} ajouté`); }} disabled={p.stock <= 0} data-testid={`add-${p.slug}`}
            className="rounded-full bg-brand text-cream h-10 w-10 flex items-center justify-center hover:bg-forest transition-colors disabled:opacity-40"><ShoppingCart size={17} /></button>
        </div>
      </div>
    </div>
  );
}

export default function Boutique() {
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [cat, setCat] = useState("all");
  const [managedCats, setManagedCats] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/shop/offers").then((r) => setOffers(r.data));
    api.get("/categories").then((r) => setManagedCats(r.data.map((c) => c.name))).catch(() => {});
  }, []);

  const derived = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));
  const cats = (managedCats.length ? managedCats : derived).filter((c) => derived.includes(c));
  const list = cat === "all" ? products : cat === "offers" ? offers : products.filter((p) => p.category === cat);

  return (
    <div className="bg-cream min-h-screen">
      <Seo title="Boutique pépinière — oliviers, plantes provençales, matériaux" path="/boutique"
        description="Commandez en ligne oliviers, plantes méditerranéennes, terre végétale, gravier et matériaux d'aménagement. Retrait à Saint-Victoret ou livraison." />

      {/* Offers banner */}
      {offers.length > 0 && (
        <section className="pt-28 md:pt-36">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="rounded-3xl bg-forest text-cream p-6 md:p-8 overflow-hidden" data-testid="offers-banner">
              <div className="flex items-center gap-2 mb-4"><Tag size={18} className="text-[#2EB82E]" /><h2 className="font-display text-2xl">Les offres du moment</h2></div>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {offers.map((p) => (
                  <Link key={p.id} to={`/boutique/${p.slug}`} className="shrink-0 w-56 rounded-2xl bg-cream/10 hover:bg-cream/20 transition-colors p-3" data-testid={`offer-${p.slug}`}>
                    {p.image && <img src={fileUrl(p.image)} alt={p.name} className="h-28 w-full object-cover rounded-xl mb-2" />}
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-sm"><span className="text-[#2EB82E] font-bold">{p.effective_price.toFixed(2)} €</span> <span className="line-through text-cream/50 text-xs">{p.price.toFixed(2)} €</span></p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className={`max-w-7xl mx-auto px-6 md:px-12 pb-24 ${offers.length ? "pt-10" : "pt-32 md:pt-40"}`}>
        {!offers.length && (
          <Reveal className="max-w-3xl mb-10"><Overline>Notre pépinière</Overline><h1 className="mt-4 font-display text-5xl lg:text-6xl text-forest">La boutique</h1></Reveal>
        )}
        <div className="grid lg:grid-cols-[240px_1fr] gap-8">
          {/* Left sidebar */}
          <aside className="lg:sticky lg:top-28 h-fit" data-testid="shop-sidebar">
            <div className="rounded-2xl bg-white border border-border p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-stone px-2 mb-2">Catégories</p>
              <button onClick={() => setCat("all")} data-testid="cat-all" className={`w-full text-left rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${cat === "all" ? "bg-brand text-cream" : "text-forest hover:bg-sage"}`}><Package size={15} /> Tous les produits</button>
              {offers.length > 0 && <button onClick={() => setCat("offers")} data-testid="cat-offers" className={`w-full text-left rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${cat === "offers" ? "bg-terracotta text-cream" : "text-terracotta hover:bg-sage"}`}><Tag size={15} /> Offres du moment</button>}
              {cats.map((c) => { const I = ICONS[c] || Leaf; return (
                <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`} className={`w-full text-left rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${cat === c ? "bg-brand text-cream" : "text-forest hover:bg-sage"}`}><I size={15} /> {c}</button>
              ); })}
            </div>
          </aside>

          <div>
            {list.length === 0 ? <div className="rounded-3xl border border-dashed border-border bg-white py-24 text-center text-stone">Aucun produit dans cette catégorie.</div> : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {list.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 0.05}><Card p={p} add={add} /></Reveal>)}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
